import { Component } from '@theme/component';

const DRAWER_OFFSET = '1.6rem';

/**
 * @typedef {object} LocalizationDrawerRefs
 * @property {HTMLDialogElement} drawer - The localization drawer dialog element.
 * @property {HTMLFormElement} form - The Shopify localization form element.
 * @property {HTMLInputElement} countryInput - Hidden input holding the country code to submit.
 * @property {HTMLFormElement[]} dialogForms - Forms with method="dialog" inside the drawer.
 */

/**
 * @extends {Component<LocalizationDrawerRefs>}
 */
class LocalizationDrawer extends Component {
  /** @type {boolean} */
  isClosing = false;
  /** @type {(() => void) | null} */
  originalClose = null;
  /** @type {HTMLElement | null} */
  backdrop = null;
  /** @type {boolean} */
  #mouseDownOnBackdrop = false;
  /** @type {NodeListOf<HTMLButtonElement> | null} */
  #triggerButtons = null;
  /** @type {number | null} */
  #outsideClickTimeout = null;

  #getDrawerSide() {
    return this.refs.drawer?.dataset.drawerSide === 'left' ? 'left' : 'right';
  }

  #getClosedTransform() {
    return this.#getDrawerSide() === 'left'
      ? `translateX(calc(-100% - ${DRAWER_OFFSET}))`
      : `translateX(calc(100% + ${DRAWER_OFFSET}))`;
  }

  #getOpenTransform() {
    return this.#getDrawerSide() === 'left' ? `translateX(${DRAWER_OFFSET})` : `translateX(-${DRAWER_OFFSET})`;
  }

  #applyInlinePosition() {
    if (!this.refs.drawer) return;

    if (this.#getDrawerSide() === 'left') {
      this.refs.drawer.style.left = '0';
      this.refs.drawer.style.right = 'auto';
      return;
    }

    this.refs.drawer.style.right = '0';
    this.refs.drawer.style.left = 'auto';
  }

  // Event handlers bound to this instance
  /** @type {(event: Event) => void} */
  _closeHandler = this.#handleClose.bind(this);
  /** @type {(event: Event) => void} */
  _cancelHandler = this.#handleCancel.bind(this);
  /** @type {(event: MouseEvent) => void} */
  _documentClickHandler = this.#handleDocumentClick.bind(this);

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.drawer || !(this.refs.drawer instanceof HTMLDialogElement)) {
      console.error('LocalizationDrawer: drawer element not found or not a dialog element');
      return;
    }

    // Get custom backdrop element
    const drawerId = this.refs.drawer.id;
    this.backdrop = document.querySelector(`[data-drawer-backdrop="${drawerId}"]`);

    this.#findTriggerButtons();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.drawer) {
      this.refs.drawer.removeEventListener('close', this._closeHandler);
      this.refs.drawer.removeEventListener('cancel', this._cancelHandler);
    }

    this.#disableOutsideClickClose();
  }

  #setupEventListeners() {
    if (!this.refs.drawer) return;

    // Save original close method first
    this.originalClose = this.refs.drawer.close.bind(this.refs.drawer);

    // Listen to native dialog events
    this.refs.drawer.addEventListener('close', this._closeHandler);
    this.refs.drawer.addEventListener('cancel', this._cancelHandler);
    // Close dialog on backdrop click
    // Check that both mousedown AND click are on the backdrop to avoid
    // closing when a drag starts in the content and ends on the backdrop
    this.refs.drawer.addEventListener('mousedown', (event) => {
      this.#mouseDownOnBackdrop = event.target === this.refs.drawer;
    });

    this.refs.drawer.addEventListener('click', (event) => {
      if (!this.refs.drawer || !this.originalClose) return;
      if (event.target === this.refs.drawer && this.#mouseDownOnBackdrop) {
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      }
      this.#mouseDownOnBackdrop = false;
    });

    // Close dialog on custom backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        if (!this.refs.drawer || !this.originalClose) return;
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      });
    }

    // Intercept form submissions that close the dialog
    if (Array.isArray(this.refs.dialogForms)) {
      for (const form of this.refs.dialogForms) {
        form.addEventListener('submit', (event) => {
          if (this.refs.drawer && this.refs.drawer.hasAttribute('open') && this.originalClose) {
            event.preventDefault();
            this.#animateClose(() => {
              if (this.originalClose) {
                this.originalClose();
              }
            });
          }
        });
      }
    }

    this.#applyInlinePosition();
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Observe open attribute changes to trigger animations
    const observer = new MutationObserver(() => {
      if (!this.refs.drawer) return;

      if (this.refs.drawer.hasAttribute('open')) {
        // Opening
        this.isClosing = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#handleOpen();
          });
        });
      } else if (!this.isClosing) {
        // Closing (only if not already closing)
        this.#handleClose();
      }
    });

    observer.observe(this.refs.drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Intercept showModal to trigger opening animation
    const originalShowModal = this.refs.drawer.showModal.bind(this.refs.drawer);
    const originalShow = this.refs.drawer.show.bind(this.refs.drawer);

    this.refs.drawer.showModal = () => {
      if (!this.refs.drawer) return;
      this.#applyInlinePosition();
      this.refs.drawer.style.transform = this.#getClosedTransform();
      originalShowModal();
      // Animation will be triggered by MutationObserver
    };

    this.refs.drawer.show = () => {
      if (!this.refs.drawer) return;
      this.#applyInlinePosition();
      this.refs.drawer.style.transform = this.#getClosedTransform();
      originalShow();
      // Animation will be triggered by MutationObserver
    };

    // Intercept close to animate before closing
    this.refs.drawer.close = () => {
      if (!this.refs.drawer || !this.originalClose) return;
      // If drawer is open, animate before closing
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          if (this.originalClose) {
            this.originalClose();
          }
        });
      } else {
        this.originalClose();
      }
    };
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick(event) {
    if (!this.refs.drawer || !this.originalClose) return;
    if (!this.refs.drawer.hasAttribute('open') || this.isClosing) return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.refs.drawer.contains(target)) return;
    if (this.backdrop?.contains(target)) return;

    const { clientX, clientY } = event;
    const rect = this.refs.drawer.getBoundingClientRect();
    const isInsideDrawer = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;

    if (isInsideDrawer) return;

    this.#animateClose(() => {
      if (this.originalClose) {
        this.originalClose();
      }
    });
  }

  #enableOutsideClickClose() {
    this.#disableOutsideClickClose();

    this.#outsideClickTimeout = window.setTimeout(() => {
      document.addEventListener('click', this._documentClickHandler, true);
      this.#outsideClickTimeout = null;
    }, 0);
  }

  #disableOutsideClickClose() {
    if (this.#outsideClickTimeout !== null) {
      window.clearTimeout(this.#outsideClickTimeout);
      this.#outsideClickTimeout = null;
    }

    document.removeEventListener('click', this._documentClickHandler, true);
  }

  #handleOpen() {
    if (!this.refs.drawer) return;

    // Show custom backdrop
    if (this.backdrop) {
      this.backdrop.classList.add('active');
    }

    this.#enableOutsideClickClose();

    // Add header--drawer-open class to header
    const header = document.querySelector('header-component');
    if (header) {
      header.classList.add('header--drawer-open');
    }

    this.#applyInlinePosition();
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Force a reflow so browser applies initial style
    this.refs.drawer.offsetHeight;

    // Use double requestAnimationFrame to ensure browser
    // has applied initial style before triggering animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.refs.drawer) return;
        // Change transform directly to trigger transition
        this.refs.drawer.style.transform = this.#getOpenTransform();
        this.refs.drawer.classList.add('drawer-shell--opening');
      });
    });

    this.#updateTriggerButtons(true);
  }

  #handleClose() {
    // This method is called after closing to clean up
    if (!this.refs.drawer) return;
    this.refs.drawer.classList.remove('drawer-shell--opening');
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Hide custom backdrop
    if (this.backdrop) {
      this.backdrop.classList.remove('active');
    }

    this.#disableOutsideClickClose();

    // Remove header--drawer-open class from header
    const header = document.querySelector('header-component');
    if (header) {
      header.classList.remove('header--drawer-open');
    }

    this.#updateTriggerButtons(false);
  }

  /**
   * @param {Event} event
   */
  #handleCancel(event) {
    event.preventDefault();
    if (!this.refs.drawer || !this.originalClose) return;
    if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
      this.#animateClose(() => {
        if (this.originalClose) {
          this.originalClose();
        }
      });
    }
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    if (!this.refs.drawer) return;

    // Mark that we're closing to avoid multiple calls
    this.isClosing = true;

    // Remove opening class
    this.refs.drawer.classList.remove('drawer-shell--opening');

    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Wait for animation to complete before closing
    setTimeout(() => {
      callback();
      this.isClosing = false;
    }, 300); // CSS transition duration (0.3s)
  }

  #findTriggerButtons() {
    // Find all buttons that control this drawer
    const drawerId = this.id;
    if (!drawerId) return;
    this.#triggerButtons = document.querySelectorAll(`[aria-controls="${drawerId}"]`);
  }

  /**
   * @param {boolean} isOpen
   */
  #updateTriggerButtons(isOpen) {
    if (!this.#triggerButtons) return;

    for (const button of this.#triggerButtons) {
      button.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    }
  }

  // Public methods for external access

  openDrawer() {
    if (!this.refs.drawer) return;
    this.refs.drawer.show();
  }

  closeDrawer() {
    if (!this.refs.drawer) return;
    this.refs.drawer.close();
  }

  /**
   * Updates the hidden country_code input when a country radio is selected.
   * Local state only — nothing is submitted until Apply.
   * @param {Event} event
   */
  handleCountryChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.refs.countryInput) return;
    this.refs.countryInput.value = input.value;
  }

  /**
   * Cancels the in-progress selection by re-selecting the currently active country.
   * @param {Event} [event]
   */
  handleReset(event) {
    event?.preventDefault();
    if (!this.refs.countryInput) return;
    this.refs.countryInput.value = this.refs.countryInput.defaultValue;

    const radios = this.querySelectorAll('input[name="localization_country"]');
    for (const radio of radios) {
      if (radio instanceof HTMLInputElement) {
        radio.checked = radio.defaultChecked;
      }
    }
  }

  /**
   * Submits the native Shopify localization form (full page reload).
   * @param {Event} [event]
   */
  handleApply(event) {
    event?.preventDefault();
    this.refs.form?.requestSubmit();
  }
}

if (!customElements.get('localization-drawer')) {
  customElements.define('localization-drawer', LocalizationDrawer);
}
